import React from 'react';
import { Button, ButtonProps } from 'antd';
import styled, { css } from 'styled-components';

export interface BusinessButtonProps extends Omit<ButtonProps, 'type'> {
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'ghost' | 'text';
  businessSize?: 'small' | 'medium' | 'large';
  gradient?: boolean;
  rounded?: boolean;
  elevated?: boolean;
}

const getButtonStyles = (variant: string, gradient: boolean) => {
  const styles = {
    primary: gradient ? css`
      background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 4px 14px rgba(64, 150, 255, 0.25);
      
      &:hover {
        background: linear-gradient(135deg, #1677ff 0%, #0958d9 100%);
        box-shadow: 0 6px 20px rgba(64, 150, 255, 0.4);
        transform: translateY(-1px);
      }
      
      &:active {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(64, 150, 255, 0.3);
      }
    ` : css`
      background: #4096ff;
      border: 1px solid #4096ff;
      color: #ffffff;
      
      &:hover {
        background: #1677ff;
        border-color: #1677ff;
      }
    `,
    
    secondary: css`
      background: #ffffff;
      border: 1px solid #d9d9d9;
      color: #1a1a2e;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      
      &:hover {
        background: #f5f5f5;
        border-color: #4096ff;
        color: #4096ff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }
    `,
    
    success: gradient ? css`
      background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 4px 14px rgba(82, 196, 26, 0.25);
      
      &:hover {
        background: linear-gradient(135deg, #389e0d 0%, #237804 100%);
        box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
        transform: translateY(-1px);
      }
    ` : css`
      background: #52c41a;
      border: 1px solid #52c41a;
      color: #ffffff;
      
      &:hover {
        background: #389e0d;
        border-color: #389e0d;
      }
    `,
    
    warning: gradient ? css`
      background: linear-gradient(135deg, #fa8c16 0%, #d46b08 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 4px 14px rgba(250, 140, 22, 0.25);
      
      &:hover {
        background: linear-gradient(135deg, #d46b08 0%, #ad4e00 100%);
        box-shadow: 0 6px 20px rgba(250, 140, 22, 0.4);
        transform: translateY(-1px);
      }
    ` : css`
      background: #fa8c16;
      border: 1px solid #fa8c16;
      color: #ffffff;
      
      &:hover {
        background: #d46b08;
        border-color: #d46b08;
      }
    `,
    
    danger: gradient ? css`
      background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
      border: none;
      color: #ffffff;
      box-shadow: 0 4px 14px rgba(255, 77, 79, 0.25);
      
      &:hover {
        background: linear-gradient(135deg, #cf1322 0%, #a8071a 100%);
        box-shadow: 0 6px 20px rgba(255, 77, 79, 0.4);
        transform: translateY(-1px);
      }
    ` : css`
      background: #ff4d4f;
      border: 1px solid #ff4d4f;
      color: #ffffff;
      
      &:hover {
        background: #cf1322;
        border-color: #cf1322;
      }
    `,
    
    ghost: css`
      background: transparent;
      border: 1px solid #4096ff;
      color: #4096ff;
      
      &:hover {
        background: rgba(64, 150, 255, 0.1);
        border-color: #1677ff;
        color: #1677ff;
      }
    `,
    
    text: css`
      background: transparent;
      border: none;
      color: #4096ff;
      box-shadow: none;
      
      &:hover {
        background: rgba(64, 150, 255, 0.1);
        color: #1677ff;
      }
    `
  };
  
  return styles[variant as keyof typeof styles] || styles.primary;
};

const getSizeStyles = (size: string) => {
  const sizes = {
    small: css`
      height: 32px;
      padding: 4px 15px;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
    `,
    medium: css`
      height: 40px;
      padding: 8px 20px;
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
    `,
    large: css`
      height: 48px;
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      line-height: 22px;
    `
  };
  
  return sizes[size as keyof typeof sizes] || sizes.medium;
};

const StyledButton = styled(Button)<{
  $variant: string;
  $businessSize: string;
  $gradient: boolean;
  $rounded: boolean;
  $elevated: boolean;
}>`
  ${props => getButtonStyles(props.$variant, props.$gradient)}
  ${props => getSizeStyles(props.$businessSize)}
  
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  border-radius: ${props => props.$rounded ? '50px' : '8px'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  ${props => props.$elevated && css`
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
    
    &:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  `}
  
  /* Ripple effect */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:active::before {
    width: 300px;
    height: 300px;
  }
  
  /* Icon spacing */
  .anticon {
    margin-right: ${props => props.children ? '8px' : '0'};
    
    &:last-child {
      margin-right: 0;
      margin-left: ${props => props.children ? '8px' : '0'};
    }
  }
  
  /* Loading state */
  &.ant-btn-loading {
    opacity: 0.8;
    cursor: not-allowed;
  }
  
  /* Disabled state */
  &:disabled,
  &.ant-btn-disabled {
    background: #f5f5f5 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    box-shadow: none !important;
    transform: none !important;
    cursor: not-allowed;
    
    &:hover {
      background: #f5f5f5 !important;
      border-color: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.25) !important;
      box-shadow: none !important;
      transform: none !important;
    }
  }
  
  /* Focus state */
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(64, 150, 255, 0.2);
  }
`;

export const BusinessButton: React.FC<BusinessButtonProps> = ({
  variant = 'primary',
  businessSize = 'medium',
  gradient = true,
  rounded = false,
  elevated = true,
  children,
  ...props
}) => {
  return (
    <StyledButton
      $variant={variant}
      $businessSize={businessSize}
      $gradient={gradient}
      $rounded={rounded}
      $elevated={elevated}
      {...props}
    >
      {children}
    </StyledButton>
  );
};
